import { formatWhen } from "@/components/boo/access-copy";
import { HostIcon } from "@/components/HostIcon";
import { Badge } from "@/components/ui/badge";
import type {
  CodeHostKind,
  ConnectorBooMove,
  ConnectorCheck,
  ConnectorHistoryFrom,
  ConnectorMain,
  ConnectorRisk,
  ConnectorSource,
  ConnectorSourceKind,
  ConnectorStatus,
  ToolState,
} from "@/lib/api";
import { hostLabel } from "@/lib/code-host";
import { RISK_LABEL, STATE_LABEL, STATUS_LABEL, monogram, movedByText, requestNumber, requestUrl, shortCommit } from "@/lib/connectors";
import type { Token, TokenKind } from "@/lib/highlight";
import { radioGroupKeyDown } from "@/lib/radio-keys";
import { cn } from "@/lib/utils";
import { AlertTriangle, Check, Circle, GitBranch, KeyRound, X } from "lucide-react";
import type { ReactNode } from "react";

export function RiskBadge({ risk, className }: { risk: ConnectorRisk; className?: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "font-normal",
        risk === "write" && "border-sky-500/50 text-sky-700 dark:text-sky-400",
        risk === "destructive" && "border-red-500/50 text-red-700 dark:text-red-400",
        risk === "read" && "text-muted-foreground",
        className,
      )}
    >
      {RISK_LABEL[risk]}
    </Badge>
  );
}

export function ConnectorStatusBadge({ status, source }: { status: ConnectorStatus; source?: ConnectorSourceKind }) {
  return (
    <Badge
      variant={status === "live" ? "secondary" : "outline"}
      className={cn(
        status === "in_review" || status === "draft" ? "border-amber-500/60 text-amber-700 dark:text-amber-400" : "",
        status === "disabled" && "text-muted-foreground",
      )}
    >
      {status === "in_review" && (source === "github" || source === "gitlab") ? "Waiting for merge" : STATUS_LABEL[status]}
    </Badge>
  );
}

// Changes Boo's own account put on main that went live with a person's later
// move, plus merges by a merge queue. They ship (the org's merge rules
// decided), so say so plainly.
export function BooMovesNote({
  moves,
  host,
  repoUrl,
}: {
  moves?: ConnectorBooMove[] | null;
  host: CodeHostKind;
  repoUrl?: string | null;
}) {
  if (!moves?.length) return null;
  const queued = moves.some((m) => m.via === "merge_queue");
  return (
    <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300" title={BOO_MOVES_HELP}>
      <AlertTriangle className="mt-px size-3.5 shrink-0" />
      <span className="text-pretty">
        Includes changes Boo’s account put on main without a person merging{queued ? ", and merges by a merge queue" : ""}:{" "}
        {moves.map((m, i) => {
          const n = m.prNumber;
          const how = MOVE_LABEL[m.via] ?? "(merged)";
          const label = n ? `${requestNumber(host, n)} ${how}` : null;
          return (
            <span key={`${m.commit}-${i}`}>
              {i > 0 && ", "}
              {!n ? (
                <>
                  <span className="font-mono">{shortCommit(m.commit)}</span> {how}
                </>
              ) : repoUrl ? (
                <a href={requestUrl(repoUrl, host, n)} target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">
                  {label}
                </a>
              ) : (
                <span className="font-medium">{label}</span>
              )}
            </span>
          );
        })}
      </span>
    </p>
  );
}

const MOVE_LABEL: Record<ConnectorBooMove["via"], string> = {
  push: "(pushed)",
  merge: "(merged)",
  merge_queue: "merged by a merge queue (who queued it isn’t known)",
};

// What the note covers, in the backend's terms.
const BOO_MOVES_HELP =
  "Lists only moves of the default branch made by Boo’s connection account: its direct pushes (force pushes included) and the requests it merged itself, plus merges made by a merge queue, whose queuer isn’t known. Commits Boo wrote or pushed to other branches reach main through whoever merges them and are credited to that person.";

// The host's records of main ended early, so moves before then went unchecked.
export function HistoryGapNote({ from }: { from?: ConnectorHistoryFrom | null }) {
  if (!from) return null;
  return (
    <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300">
      <AlertTriangle className="mt-px size-3.5 shrink-0" />
      <span className="text-pretty">
        Couldn’t check changes to main before {new Date(from.at).toLocaleDateString(undefined, { dateStyle: "medium" })}; Boo’s
        account may have moved it then.
      </span>
    </p>
  );
}

// Code host: what's on the default branch and whether it runs, e.g.
// "main 6893e50 · merged by rita (!4)", or why it doesn't.
export function MainStatus({ main, host, repoUrl }: { main: ConnectorMain; host: CodeHostKind; repoUrl?: string | null }) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-start gap-x-2 gap-y-0.5 rounded-lg border px-3 py-2 text-sm",
        !main.live && "border-amber-500/50 bg-amber-50/60 dark:bg-amber-950/20",
      )}
    >
      <GitBranch className={cn("mt-0.5 size-4 shrink-0", main.live ? "text-emerald-600" : "text-amber-600")} />
      <div className="min-w-0 flex-1">
        <p>
          <span className="font-medium">{main.branch}</span>{" "}
          <code className="font-mono text-xs text-muted-foreground">{shortCommit(main.commit)}</code>
          {main.live ? (
            <span className="text-muted-foreground">
              {" "}
              · runs
              {main.movedBy && (
                <>
                  {" "}
                  · {main.movedBy.prUrl && main.movedBy.via === "merge" ? (
                    <a href={main.movedBy.prUrl} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline">
                      {movedByText(main.movedBy, host)}
                    </a>
                  ) : (
                    movedByText(main.movedBy, host)
                  )}
                </>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground"> · not running</span>
          )}
        </p>
        {!main.live && main.reason && <p className="text-xs text-muted-foreground text-pretty">{main.reason}</p>}
        {main.live && <BooMovesNote moves={main.includesBooMoves} host={host} repoUrl={repoUrl} />}
        <HistoryGapNote from={main.historyFrom} />
      </div>
      <span className="shrink-0 text-xs text-muted-foreground">
        Checked on {hostLabel(host)} {formatWhen(main.checkedAt)}
      </span>
    </div>
  );
}

export function NewVersionBadge() {
  return (
    <Badge variant="outline" className="border-amber-500/60 text-amber-700 dark:text-amber-400">
      New version to review
    </Badge>
  );
}

export function SourceLabel({ source }: { source: ConnectorSource }) {
  if (source.kind === "boo") return <span>Kept by Boo</span>;
  // Inline, not inline-flex: an inline-flex box takes its baseline from the
  // icon and rides above the text around it.
  return (
    <span className="whitespace-nowrap">
      <HostIcon host={source.kind} className="mr-1 inline-block size-3 align-[-0.125em]" />
      {hostLabel(source.kind)}
    </span>
  );
}

// A row of radio buttons, one selected; arrow keys move the selection.
// activeClassName styles the selected option (default: raised).
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled,
  activeClassName,
}: {
  label: string;
  options: readonly { value: T; label: ReactNode }[];
  value: T;
  onChange: (next: T) => void;
  disabled?: boolean;
  activeClassName?: (value: T) => string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={radioGroupKeyDown}
      className="inline-flex w-fit shrink-0 rounded-md border bg-muted/40 p-0.5"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          tabIndex={value === opt.value ? 0 : -1}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          className={cn(
            "rounded px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60",
            value === opt.value
              ? (activeClassName?.(opt.value) ?? "bg-background text-foreground shadow-sm")
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

const STATE_OPTIONS = (["on", "ask", "off"] as const).map((value) => ({ value, label: STATE_LABEL[value] }));

// On / Ask first / Off for one action.
export function StateToggle(props: { value: ToolState; onChange: (next: ToolState) => void; disabled?: boolean; label: string }) {
  return (
    <Segmented
      {...props}
      options={STATE_OPTIONS}
      activeClassName={(opt) =>
        opt === "off"
          ? "bg-background text-foreground shadow-sm"
          : opt === "ask"
            ? "bg-amber-500/90 text-white shadow-sm"
            : "bg-primary text-primary-foreground shadow-sm"
      }
    />
  );
}

export function SecretBadge({ children = "Secret", className }: { children?: ReactNode; className?: string }) {
  return (
    <Badge variant="outline" className={cn("gap-1 border-amber-500/60 text-amber-700 dark:text-amber-400", className)}>
      <KeyRound className="size-3" />
      {children}
    </Badge>
  );
}

export function ConnectorMonogram({ name, small }: { name: string; small?: boolean }) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-md bg-muted font-semibold",
        small ? "size-8 text-xs" : "size-10 text-sm",
      )}
    >
      {monogram(name)}
    </div>
  );
}

// "Connect Acme Billing / So Ops Boo can use it", atop the form-only pages.
export function ConnectHeading({ name, booName }: { name: string; booName: string }) {
  return (
    <div className="flex items-center gap-3">
      <ConnectorMonogram name={name} />
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-lg font-semibold">Connect {name}</h1>
        <p className="truncate text-sm text-muted-foreground">So {booName} can use it</p>
      </div>
    </div>
  );
}

export function ChecksList({ checks, pending }: { checks: ConnectorCheck[]; pending?: boolean }) {
  return (
    <ul className="divide-y rounded-lg border">
      {checks.map((c) => (
        <li key={c.name} className="flex items-start gap-2.5 p-3">
          {pending ? (
            <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          ) : c.ok ? (
            <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" />
          ) : (
            <X className="mt-0.5 size-4 shrink-0 text-destructive" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium leading-tight">{c.name}</p>
            {c.message && <p className="mt-0.5 text-xs text-muted-foreground">{c.message}</p>}
          </div>
        </li>
      ))}
    </ul>
  );
}

const TOKEN_CLASS: Record<TokenKind, string> = {
  comment: "text-muted-foreground italic",
  string: "text-emerald-700 dark:text-emerald-400",
  keyword: "text-violet-700 dark:text-violet-400",
  number: "text-amber-700 dark:text-amber-400",
  literal: "text-amber-700 dark:text-amber-400",
  type: "text-sky-700 dark:text-sky-400",
  fn: "text-blue-700 dark:text-blue-400",
  key: "text-sky-700 dark:text-sky-400",
  heading: "font-semibold text-foreground",
};

// One highlighted line of code (lib/highlight tokens).
export function CodeTokens({ tokens }: { tokens: Token[] }) {
  if (tokens.length === 0) return " ";
  return tokens.map((t, k) =>
    t.kind ? (
      <span key={k} className={TOKEN_CLASS[t.kind]}>
        {t.text}
      </span>
    ) : (
      t.text
    ),
  );
}
